<template>
  <div class="container" :style="overlayBgStyle">
    <div class="close-button">
      <q-btn icon="close" flat round dense @click="$emit('closeFlipBook')" />
      <ReturnHomeButton @click="$emit('closeAllDialogs')" />
    </div>

    <main class="main-content">
      <!-- Vertical Control Panel on the left -->
      <div class="control-panel">
        <!-- Zoom Controls -->
        <div class="control-section">
          <q-btn 
            icon="add" 
            flat 
            round
            size="lg"
            :color="scaleValue >= 3 ? 'grey-5' : 'black'"
            :disable="scaleValue >= 3"
            @click="zoomIn"
            class="control-btn"
          />
          <div class="zoom-level" @click="resetZoom">
            {{ Math.round(scaleValue * 100) }}%
          </div>
          <q-btn 
            icon="remove" 
            flat 
            round
            size="lg"
            :color="scaleValue <= 1 ? 'grey-5' : 'black'"
            :disable="scaleValue <= 1"
            @click="zoomOut"
            class="control-btn"
          />
        </div>

        <div class="divider"></div>

        <!-- Navigation Controls -->
        <div class="control-section nav-section">
          <q-btn 
            icon="keyboard_arrow_up" 
            flat 
            round
            size="md"
            color="black"
            @click="scrollCanvas('up')"
            class="nav-btn"
          />
          <div class="nav-horizontal">
            <q-btn 
              icon="keyboard_arrow_left" 
              flat 
              round
              size="md"
              color="black"
              @click="scrollCanvas('left')"
              class="nav-btn"
            />
            <q-btn 
              icon="adjust" 
              flat 
              round
              size="sm"
              color="grey-6"
              @click="centerView"
              class="nav-btn center-btn"
            />
            <q-btn 
              icon="keyboard_arrow_right" 
              flat 
              round
              size="md"
              color="black"
              @click="scrollCanvas('right')"
              class="nav-btn"
            />
          </div>
          <q-btn 
            icon="keyboard_arrow_down" 
            flat 
            round
            size="md"
            color="black"
            @click="scrollCanvas('down')"
            class="nav-btn"
          />
        </div>

        <div class="divider"></div>

        <!-- Additional Controls -->
        <div class="control-section">
          <q-btn 
            icon="aspect_ratio" 
            flat 
            round
            size="lg"
            color="grey-7"
            @click="fitToScreen"
            class="control-btn"
          />
          <q-btn 
            :icon="isFullscreen ? 'fullscreen_exit' : 'fullscreen'" 
            flat 
            round
            size="lg"
            color="grey-7"
            @click="toggleFullscreen"
            class="control-btn"
          />
        </div>
      </div>

      <div class="pageflip-container">
        <div class="flipHint">
          <span>→</span> Cliquez pour tourner la page
        </div>

        <div ref="pageflip" class="pageflip"></div>
      </div>
    </main>
  </div>
</template>
  
<script>
import { PageFlip } from 'page-flip';
import { ref, onMounted } from "vue";
import Hammer from 'hammerjs';
import ReturnHomeButton from './ReturnHomeButton.vue';
import { useOverlayBg } from '../composables/useOverlayBg';
import axios from 'axios';

export default {
  components: { ReturnHomeButton },
  props: ['blockDatas'],

  setup(props) {
    const pageflip = ref(null);
    const pages = ref([])

    // Une page = l'image réduite pour tenir dans la page, centrée sur fond
    // blanc (PageFlip étire l'image à la taille de la page : sans ces marges
    // elle serait déformée).
    //
    // Le redimensionnement passait par pica, qui relit le canvas et vérifie
    // d'abord que cette relecture est fidèle. Les navigateurs qui brouillent
    // les relectures de canvas contre le pistage (réglage anti-empreinte de
    // Chrome, Brave, Firefox) échouent à ce contrôle : pica levait une erreur
    // et le livre ne se construisait pas du tout. Un seul drawImage lissé
    // suffit ici, et ne dépend d'aucune relecture.
    async function imageEnPage(url, largeur, hauteur) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = url;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      const ratio = Math.min(largeur / img.width, hauteur / img.height);
      const l = img.width * ratio;
      const h = img.height * ratio;

      const canvas = document.createElement('canvas');
      canvas.width = largeur;
      canvas.height = hauteur;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, largeur, hauteur);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, (largeur - l) / 2, (hauteur - h) / 2, l, h);

      return canvas.toDataURL();
    }
    
    const scaleValue = ref(1); // Valeur initiale
    const isFullscreen = ref(false);

    function applyTransform() {
      if (pageflip.value) {
        pageflip.value.style.transform = `scale(${scaleValue.value}) translate(${translation.value.x}px, ${translation.value.y}px)`;
        pageflip.value.style.transition = 'transform 0.3s ease';
      }
    }

    const zoomIn = () => {
      if (scaleValue.value < 3) {
        scaleValue.value = Math.min(scaleValue.value + 0.25, 3);
        applyTransform();
      }
    };

    const zoomOut = () => {
      if (scaleValue.value > 1) {
        scaleValue.value = Math.max(scaleValue.value - 0.25, 1);
        if (scaleValue.value === 1) {
          translation.value = { x: 0, y: 0 };
        }
        applyTransform();
      }
    };

    const resetZoom = () => {
      scaleValue.value = 1;
      translation.value = { x: 0, y: 0 };
      applyTransform();
    };

    const translation = ref({ x: 0, y: 0 });

    const scrollCanvas = (direction) => {
      const scrollAmount = scaleValue.value > 1 ? 75 : 50;

      switch (direction) {
        case 'up':
          translation.value.y -= scrollAmount;
          break;
        case 'down':
          translation.value.y += scrollAmount;
          break;
        case 'left':
          translation.value.x -= scrollAmount;
          break;
        case 'right':
          translation.value.x += scrollAmount;
          break;
      }

      applyTransform();
    };

    const centerView = () => {
      translation.value = { x: 0, y: 0 };
      applyTransform();
    };

    const fitToScreen = () => {
      scaleValue.value = 1;
      translation.value = { x: 0, y: 0 };
      applyTransform();
    };

    const toggleFullscreen = () => {
      const elem = document.querySelector('.pageflip-container');
      if (!document.fullscreenElement) {
        elem.requestFullscreen().then(() => {
          isFullscreen.value = true;
        }).catch(err => {
          console.error('Erreur fullscreen:', err);
        });
      } else {
        document.exitFullscreen().then(() => {
          isFullscreen.value = false;
        });
      }
    }

    onMounted(async () => {
      const options = {
        width: 800,
        height: 600,
        showCover: true,
        usePortrait: true,
      };

      for (let i = 0; i < props.blockDatas.associatedResources.length; i++) {
        // Une page illisible ne doit pas empêcher d'afficher les autres.
        try {
          let url = `${process.env.VUE_APP_BASEURL}/s3Resource/${props.blockDatas.associatedResources[i].name}`;
          let response = await axios.get(url);
          pages.value.push(await imageEnPage(response.data, 800, 600));
        } catch (e) {
          console.warn('FlipBook : page ignorée', props.blockDatas.associatedResources[i].name, e);
        }
      }

      const pageFlipInstance = new PageFlip(pageflip.value, options);
      pageFlipInstance.loadFromImages(pages.value);
      const hammer = new Hammer(pageflip.value);
      hammer.get('pinch').set({ enable: true });

      hammer.on('pinchin', zoomOut);
      hammer.on('pinchout', zoomIn);
    });

    const { overlayBgStyle } = useOverlayBg();

    return {
      overlayBgStyle,
      pageflip,
      pages,
      scaleValue,
      isFullscreen,
      zoomIn,
      zoomOut,
      resetZoom,
      scrollCanvas,
      centerView,
      fitToScreen,
      toggleFullscreen
    };
  },
};
</script>
  
<style scoped>
/* Main Container */
.container {
  background-color: rgba(245, 245, 245, 0.95);
  position: fixed;
  left: 0;
  top: 0;
  z-index: 9999999;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start; /* Align content to the top */
}

.main-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-top: 80px; /* Add margin to push content down from the close button */
}

/* Close button */
.close-button {
  position: absolute; /* Changed from fixed */
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10001;
  display: flex;
  gap: 10px;
}

/* Vertical Control Panel */
.control-panel {
  position: relative; /* Changed from fixed */
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 10px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 25px;
  backdrop-filter: blur(5px);
  flex-shrink: 0;
}

/* Control sections */
.control-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

/* Navigation specific layout */
.nav-section {
  gap: 5px;
}

.nav-horizontal {
  display: flex;
  align-items: center;
  gap: 5px;
}

/* Buttons */
.control-btn, .nav-btn {
  transition: all 0.2s ease;
  touch-action: manipulation;
}

.control-btn:active, .nav-btn:active {
  transform: scale(0.95);
  background: rgba(33, 150, 243, 0.1);
}

.center-btn {
  opacity: 0.7;
}

.center-btn:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.05);
}

/* Zoom level indicator */
.zoom-level {
  font-size: 14px;
  font-weight: 600;
  color: #424242;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 20px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  min-width: 60px;
  text-align: center;
}

.zoom-level:hover {
  background: rgba(33, 150, 243, 0.1);
}

.zoom-level:active {
  transform: scale(0.95);
}

/* Divider */
.divider {
  width: 40px;
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
  margin: 5px 0;
}

/* Pageflip Container */
.pageflip-container {
  position: relative; /* Changed from absolute */
  z-index: 1; /* Lower z-index */
  width: 800px;
  height: 600px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  overflow: hidden;
  transform-origin: center center;
  background: white;
}

.pageflip-container:fullscreen {
  width: 100vw;
  height: 100vh;
  border-radius: 0;
}

.pageflip {
  width: 100%;
  height: 100%;
  transform-origin: center center;
}

.flipHint {
  text-align: right;
  font-weight: bold;
  margin-right: 1%;
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 13px;
  z-index: 10000;
  animation: fadeInOut 4s ease-in-out;
  pointer-events: none; /* Keep this from previous fix */
}

@keyframes fadeInOut {
  0%, 100% { opacity: 0; }
  15%, 85% { opacity: 1; }
}

img {
  max-width: 100%;
  height: auto;
}

/* Touch-friendly adjustments for mobile */
@media (max-width: 1024px) { /* Adjust breakpoint */
  .main-content {
    flex-direction: column;
    margin-top: 80px;
  }
  .control-panel {
    flex-direction: row;
    bottom: 20px;
    padding: 10px 20px;
    width: auto;
  }
  .control-section {
    flex-direction: row;
  }
  .divider {
    width: 1px;
    height: 30px;
  }
}

/* Button disabled state */
.q-btn--disable {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>